import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { mensagemDoErro } from '../api';
import { pesarSeca, verEstufaDoEnsaio } from '../api/triviais';
import type { EstufaDoEnsaio } from '../api/triviais';
import { numero, paraNumero } from '../formato';
import { useSessao } from '../sessao';
import { CampoNumerico, Selo } from '../ui';
import type { Opcao } from '../ui';
import {
  JanelaDeCorrecao, correcaoPossivel, tempoDeEstufa, useRelogio,
} from './Triviais';
import type { Correcao } from './Triviais';
import { SeloDaEstufa, useAlertaDaEstufa } from './alertaDaEstufa';

/**
 * A Estufa dentro de cada ficha (onda 294, RN-241).
 *
 * «A Estufa atenderá todos os ensaios que necessitam a execução da medição
 * de umidade, que são praticamente todos» (Humberto, 07/10/2026). Ele
 * escolheu o mesmo padrão dos triviais, SEM etapa nova: nas fichas da
 * compactação, do adensamento, do cisalhamento direto, do triaxial, da
 * granulometria e da massa específica a cápsula se grava com a massa
 * úmida, e a coluna da massa seca mostra «na estufa» até alguém pesá-la —
 * ali mesmo, ou pela tela Estufa. As duas portas gravam pela mesma rota
 * (`POST /estufa/pesagens`), e por isso não há pesagem seca em dobro.
 *
 * O carimbo (quem pesou, quando, até quando se corrige) não está nas
 * visões das seis fichas: vem de `GET /estufa/ensaio/:id`, a mesma linha
 * de cápsula que a Estufa lê (`vw_linha_de_capsula`, 0246).
 */

const FUSO = 'America/Sao_Paulo';

/** «07/10 09:38» — a hora curta da bancada. */
const quando = (v: unknown) => {
  if (!v) return '—';
  const d = new Date(String(v));
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('pt-BR', { timeZone: FUSO, day: '2-digit', month: '2-digit',
    hour: '2-digit', minute: '2-digit' }).replace(',', '');
};
const hora = (ms: number) => new Date(ms).toLocaleTimeString('pt-BR',
  { timeZone: FUSO, hour: '2-digit', minute: '2-digit' });

const descartadaDe = (l: any) => Boolean(l?.descartada || l?.descartado);

/** A linha da ficha que ainda espera a pesagem seca. */
export const naEstufaDaFicha = (l: any) =>
  !descartadaDe(l) && l?.massa_capsula_solo_seco == null;

export interface Estufa {
  /** linha_id → a linha de cápsula com o carimbo (e `id` = linha_id). */
  porId: Map<string, any>;
  agora: number;
  /** Depois de quantas horas a cápsula entra em alerta (RN-250, onda 305). */
  alertaHoras: number;
  euId?: string;
  podeTravado: boolean;
  recarregar(): Promise<void>;
}

/**
 * As linhas de cápsula do ensaio, recarregadas a cada `versao` — a ficha
 * aumenta a versão a cada gravação, e o carimbo acompanha.
 */
export function useEstufaDoEnsaio(ensaioId: string | undefined, versao: unknown): Estufa {
  const { eu, pode } = useSessao();
  const [d, setD] = useState<EstufaDoEnsaio | null>(null);
  /*
   * O aviso à barra (Rulings R25, R33, R38). A ficha relê as cápsulas depois
   * de cada gravação; se o conjunto das que estão na estufa mudou — entrou
   * uma (a pesagem úmida), saiu uma (a seca, o descarte, o ponto
   * descartado) —, o número da barra (308) relê. É o aviso das seis fichas
   * que usam este gancho; a SecaDaLinha não avisa de novo.
   *
   *  · A `base` é o conjunto da leitura anterior. A primeira leitura só a
   *    marca, sem avisar; depois, uma vez por gravação — a leitura seguinte
   *    já acha o mesmo conjunto.
   *  · As leituras vão uma de cada vez, na ordem pedida (`fila`): a de antes
   *    da gravação nunca chega depois da de depois, e nenhuma é jogada fora.
   *  · Sem base, a leitura que falhou tenta de novo (até 5 vezes, de segundo
   *    em segundo) antes de a fila andar: a gravação seguinte tem com o que
   *    comparar.
   *  · Se a base não veio (a Estufa fora do ar por mais tempo), o que for
   *    pedido depois da primeira falha pode ser a releitura de uma gravação:
   *    a primeira leitura boa avisa (`avisarNaBase`, Ruling R41). O aviso a
   *    mais não faz mal — a barra só relê —; o que falta faz.
   *  · A `geracao` muda com o ensaio e quando a ficha sai: leitura de outro
   *    ensaio, ou de uma tela que já fechou, não conta.
   */
  const base = useRef<string | null>(null);
  const semBase = useRef(false);
  const avisarNaBase = useRef(false);
  const fila = useRef<Promise<void>>(Promise.resolve());
  const geracao = useRef(0);
  useEffect(() => {
    geracao.current += 1;
    base.current = null;
    semBase.current = false;
    avisarNaBase.current = false;
    return () => { geracao.current += 1; };
  }, [ensaioId]);
  const recarregar = useCallback(() => {
    if (!ensaioId) return Promise.resolve();
    const g = geracao.current;
    if (base.current === null && semBase.current) avisarNaBase.current = true;
    const ler = async () => {
      for (let tentativa = 1; g === geracao.current; tentativa += 1) {
        try {
          const e = await verEstufaDoEnsaio(ensaioId);
          if (g !== geracao.current) return;
          setD(e);
          const conjunto = (e.linhas ?? []).filter((l: any) => l.na_estufa)
            .map((l: any) => String(l.linha_id)).sort().join(',');
          if (base.current === null ? avisarNaBase.current : base.current !== conjunto) {
            window.dispatchEvent(new CustomEvent('labmanager:estufa-mudou'));
          }
          base.current = conjunto;
          avisarNaBase.current = false;
          return;
        } catch {
          if (g !== geracao.current) return;
          /* Sem o carimbo a ficha continua: a pesagem e a trava são do
             servidor. Já com base, a próxima gravação relê e compara. A
             falha de uma leitura de outro ensaio (ou de uma tela que saiu)
             não marca nada nem tenta de novo, acima. */
          if (base.current === null) semBase.current = true;
          if (base.current !== null || tentativa >= 5) return;
          await new Promise((r) => { window.setTimeout(r, 1000); });
        }
      }
    };
    fila.current = fila.current.then(ler);
    return fila.current;
  }, [ensaioId]);
  useEffect(() => { void recarregar(); }, [recarregar, versao]);
  const agora = useRelogio(d?.agora);
  const alertaHoras = useAlertaDaEstufa(d?.alertaHoras);
  const porId = useMemo(() => new Map((d?.linhas ?? []).map(
    (l: any) => [l.linha_id as string, { ...l, id: l.linha_id }])), [d]);
  return {
    porId, agora, alertaHoras, euId: eu?.usuarioId,
    podeTravado: pode('ensaio.corrigir_lancamento'), recarregar,
  };
}

/**
 * O que a ficha diz embaixo da tabela: a seca pode ficar para depois.
 */
export function LembreteDaEstufa({ quantas }: { quantas: number }) {
  return (
    <p className="fe-lembrete ne-lembrete">
      A massa seca pode ficar em branco: a cápsula vai para a estufa e se pesa
      depois, aqui na coluna da massa seca ou pela <Link to="/estufa">Estufa</Link>.
      {quantas > 0 && <> <b>{quantas} na estufa</b> agora.</>}
    </p>
  );
}

/**
 * A célula da massa seca de uma linha GRAVADA.
 *
 *  · na estufa: o campo da massa seca e «Pesar» (Enter também pesa), com o
 *    tempo de estufa — e «Corrigir» a úmida, se ainda dá;
 *  · pesada: a massa, quem pesou e quando, e «Corrigir» enquanto a janela
 *    está aberta (ou para quem tem a permissão);
 *  · descartada, ou o ensaio só leitura: só o que está gravado.
 *
 * A tela só OFERECE: quem decide se a correção passa é o banco (RN-236).
 */
export function SecaDaLinha({
  linha, estufa, congelado, opcoesCapsula, casas = 2, aoMudou,
}: {
  linha: any;
  estufa: Estufa;
  congelado?: boolean;
  opcoesCapsula: Opcao[];
  casas?: number;
  aoMudou(): unknown;
}) {
  const [valor, setValor] = useState('');
  const [pesando, setPesando] = useState(false);
  const [erro, setErro] = useState<unknown>(null);
  const [alvo, setAlvo] = useState<{ linha: any; c: Correcao } | null>(null);
  const l = estufa.porId.get(linha.id);
  const seco = linha.massa_capsula_solo_seco;
  const { agora, euId, podeTravado } = estufa;

  /* A releitura da Estufa vai para a fila dela e não segura o «Gravando…»:
     com a Estufa fora do ar, a fila espera as releituras da base (até 5 s).
     A ficha recarrega já; o carimbo e o aviso à barra chegam com a fila. */
  const depois = async () => { void estufa.recarregar(); await aoMudou(); };

  const pesar = async () => {
    const massa = paraNumero(valor);
    if (massa == null || pesando) return;
    setPesando(true); setErro(null);
    try {
      await pesarSeca({ linhaId: linha.id, massaCapsulaSoloSeco: massa });
      setValor('');
      /* Uma cápsula a menos na estufa: o `useEstufaDoEnsaio` relê aqui e
         avisa a barra (Ruling R25) — avisar também daqui seria em dobro. */
      await depois();
    } catch (e) { setErro(e); } finally { setPesando(false); }
  };

  const janela = alvo && (
    <JanelaDeCorrecao alvo={alvo} tipo={alvo.linha.tipo} opcoesCapsula={opcoesCapsula}
      aoFechar={() => setAlvo(null)} aoGravado={() => void depois()} />
  );

  if (descartadaDe(linha)) return <>{seco == null ? '—' : numero(seco, casas)}</>;

  if (seco != null) {
    const corr = l && !congelado
      ? correcaoPossivel(l, 'seca', euId, podeTravado, agora) : null;
    return (
      <span className="ne-seca">
        <span className="n">{numero(seco, casas)}</span>
        {l?.seco_em && <span className="dd-quem">{quando(l.seco_em)} · {l.seco_por_nome ?? ''}</span>}
        {corr && (
          <button type="button" className="botao discreto pequeno ne-corrigir"
            aria-label={`Corrigir a pesagem seca da cápsula ${linha.capsula ?? ''}`}
            onClick={() => setAlvo({ linha: l, c: corr })}>
            {corr.modo === 'janela' ? `Corrigir · até ${hora(corr.ate!)}` : 'Corrigir'}
          </button>
        )}
        {janela}
      </span>
    );
  }

  const tempo = l ? tempoDeEstufa(l.umido_em, agora) : null;
  /* RN-250 (onda 305): passou das horas do ajuste, o selo do alerta — a
     pesagem continua aceita. */
  const alerta = l ? <SeloDaEstufa desde={l.umido_em} agora={agora} horas={estufa.alertaHoras} /> : null;
  if (congelado) {
    return (
      <>
        <Selo tom="atencao">Na estufa{tempo && tempo !== '—' ? ` · ${tempo}` : ''}</Selo>
        {alerta}
      </>
    );
  }
  const corrUmida = l ? correcaoPossivel(l, 'umida', euId, podeTravado, agora) : null;
  return (
    <span className="ne-seca ne-pendente">
      <CampoNumerico rotulo={`Cápsula + solo seco — ${linha.capsula ?? ''}`} rotuloOculto
        data-seca="1" enterKeyHint="done" placeholder="na estufa"
        value={valor} disabled={pesando}
        onChange={(e) => setValor(e.target.value)}
        onKeyDown={(e) => {
          /* Numa tabela de lançamento quem cuida do Enter é a tabela: grava
             pelo «Pesar» e pula para a linha seguinte (RN-275). */
          if (e.key === 'Enter' && !e.currentTarget.closest('[data-teclado]')) {
            e.preventDefault(); void pesar();
          }
        }} />
      <button type="button" className="botao pequeno ne-pesar" data-gravar-linha=""
        aria-label={`Pesar seca a cápsula ${linha.capsula ?? ''}`}
        aria-disabled={!valor.trim() || pesando}
        onClick={() => void pesar()}>
        {pesando ? 'Gravando…' : 'Pesar'}
      </button>
      <span className="dd-quem">na estufa{tempo && tempo !== '—' ? ` · ${tempo}` : ''}</span>
      {alerta}
      {corrUmida && (
        <button type="button" className="botao discreto pequeno ne-corrigir"
          aria-label={`Corrigir a pesagem úmida da cápsula ${linha.capsula ?? ''}`}
          onClick={() => setAlvo({ linha: l, c: corrUmida })}>
          {corrUmida.modo === 'janela' ? `Corrigir a úmida · até ${hora(corrUmida.ate!)}` : 'Corrigir a úmida'}
        </button>
      )}
      {erro ? <span className="erro ne-erro" role="alert">{mensagemDoErro(erro)}</span> : null}
      {janela}
    </span>
  );
}

/** O w da linha, ou «na estufa» enquanto falta a seca. */
export function WDaLinha({ linha, casas = 2 }: { linha: any; casas?: number }) {
  if (descartadaDe(linha)) return <>—</>;
  if (linha.massa_capsula_solo_seco == null) return <span className="ne-espera">na estufa</span>;
  return <>{numero(linha.umidade, casas)}</>;
}
